import { Scanner, type IDetectedBarcode } from "@yudiel/react-qr-scanner";
import {
  AlertTriangle,
  ArrowLeft,
  Ban,
  Camera,
  CheckCircle2,
  Keyboard,
  KeyRound,
  Loader2,
  LogOut,
  QrCode,
  RefreshCcw,
  RotateCcw,
  Search,
  ShieldAlert,
  Ticket,
  XCircle,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { DashboardShell } from "@/components/dashboard-shell";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ChangePasswordDialog } from "@/features/organizers/change-password-dialog";
import { useAuthenticatedMe, useLogoutMutation } from "@/features/organizers/api";
import {
  useCheckInEventStatsQuery,
  useCheckInEventsQuery,
  useScanTicketMutation,
} from "@/features/check-in/api";
import { ISTANBUL_TZ, useFormat, type Format } from "@/lib/format";
import { useT, type TFunction } from "@/lib/i18n";
import { cn } from "@/lib/utils";
import type {
  CheckInEventListItem,
  ScanResult,
  ScanTicketResponse,
} from "@ticketnjoy/types";

/**
 * QR Check-in PWA — kapı operatörü ekranı.
 *
 * Akış:
 * 1) Etkinlik seç (Gate role TÜM etkinlikleri görür, MVP'de event-bazlı kapı yok).
 * 2) Kamera açılır, sürekli QR tarar; başarılı okumada API çağrısı.
 * 3) Sonuç: full-screen yeşil/sarı/kırmızı flash + vibrate + beep.
 * 4) "Devam et" veya 3sn sonra otomatik resume.
 *
 * Online-only MVP — internet kesilirse hata gösterilir, offline queue v1.5'te.
 */
export function CheckInPage() {
  const { data: me, isLoading } = useAuthenticatedMe();
  const logout = useLogoutMutation();
  const t = useT("checkIn");

  const [selectedEventId, setSelectedEventId] = useState<string | null>(null);
  const [manualMode, setManualMode] = useState(false);

  if (isLoading || !me) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-muted/40">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Sadece Gate role full-screen PWA görür (kapı operatörü); diğer roller
  // dashboard shell içinde kalır, sidebar/menü kaybolmaz.
  const isGateOnly = me.role === "Gate";

  const onBack = () => {
    setSelectedEventId(null);
    setManualMode(false);
  };

  const body = (
    <>
      {selectedEventId ? (
        <ScannerScreen
          eventId={selectedEventId}
          manualMode={manualMode}
          onToggleManual={() => setManualMode((v) => !v)}
        />
      ) : (
        <EventPickerScreen
          onSelect={(id) => {
            setSelectedEventId(id);
            setManualMode(false);
          }}
        />
      )}
    </>
  );

  if (isGateOnly) {
    return (
      <div className="min-h-screen bg-muted/40">
        <div className="mx-auto flex max-w-md flex-col gap-3 p-3 sm:p-4">
          <Header
            isGateOnly
            showBack={!!selectedEventId}
            onBack={onBack}
            onLogout={() => logout.mutateAsync()}
            loggingOut={logout.isPending}
          />
          {body}
        </div>
      </div>
    );
  }

  // Owner | Manager | Editor — dashboard shell içinde, sidebar açık kalır.
  return (
    <DashboardShell companyName={me.companyName} role={me.role}>
      <div className="mx-auto flex w-full max-w-md flex-col gap-3">
        {selectedEventId ? (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onBack}
            className="self-start"
          >
            <ArrowLeft className="me-1 h-4 w-4 rtl:-scale-x-100" />
            {t("backToEvents")}
          </Button>
        ) : (
          <div className="space-y-1">
            <h1 className="text-xl font-semibold">{t("title")}</h1>
            <p className="text-sm text-muted-foreground">{t("subtitle")}</p>
          </div>
        )}
        {body}
      </div>
    </DashboardShell>
  );
}

/* ───────────── Header ───────────── */

interface HeaderProps {
  isGateOnly: boolean;
  showBack: boolean;
  onBack: () => void;
  onLogout: () => void | Promise<unknown>;
  loggingOut: boolean;
}

function Header({ isGateOnly, showBack, onBack, onLogout, loggingOut }: HeaderProps) {
  const t = useT("checkIn");
  const tPassword = useT("auth.changePassword");
  const tCommon = useT("common");
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2">
        {showBack ? (
          <Button size="icon" variant="ghost" onClick={onBack} aria-label={tCommon("back")}>
            <ArrowLeft className="h-5 w-5 rtl:-scale-x-100" />
          </Button>
        ) : (
          <div className="flex h-9 w-9 items-center justify-center rounded-md bg-primary/10 text-primary">
            <QrCode className="h-5 w-5" />
          </div>
        )}
        <div>
          <div className="text-base font-semibold">{t("title")}</div>
          <div className="text-xs text-muted-foreground">{t("subtitle")}</div>
        </div>
      </div>
      {isGateOnly ? (
        <div className="flex items-center gap-2">
          <Button
            size="icon"
            variant="outline"
            onClick={() => setChangePasswordOpen(true)}
            aria-label={tPassword("button")}
            title={tPassword("button")}
          >
            <KeyRound className="h-4 w-4" />
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => onLogout()}
            disabled={loggingOut}
          >
            <LogOut className="me-1 h-4 w-4 rtl:-scale-x-100" />
            {t("logout")}
          </Button>
          <ChangePasswordDialog open={changePasswordOpen} onOpenChange={setChangePasswordOpen} />
        </div>
      ) : null}
    </div>
  );
}

/* ───────────── Event picker ───────────── */

interface EventPickerScreenProps {
  onSelect: (id: string) => void;
}

function EventPickerScreen({ onSelect }: EventPickerScreenProps) {
  const { data, isLoading, isError, refetch, isFetching } = useCheckInEventsQuery();
  const t = useT("checkIn");
  const tCommon = useT("common");

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("pickTitle")}</CardTitle>
        <CardDescription>{t("pickDescription")}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : isError ? (
          <div className="space-y-2 text-center">
            <p className="text-sm text-destructive">{t("loadError")}</p>
            <Button size="sm" variant="outline" onClick={() => refetch()}>
              <RefreshCcw className="me-1 h-4 w-4" />
              {tCommon("retry")}
            </Button>
          </div>
        ) : !data || data.length === 0 ? (
          <p className="text-center text-sm text-muted-foreground">{t("empty")}</p>
        ) : (
          <ul className="space-y-2">
            {data.map((evt) => (
              <li key={evt.id}>
                <EventRow event={evt} onSelect={() => onSelect(evt.id)} />
              </li>
            ))}
          </ul>
        )}
        <Button
          variant="ghost"
          size="sm"
          className="w-full"
          onClick={() => refetch()}
          disabled={isFetching}
        >
          <RefreshCcw className={cn("me-1 h-4 w-4", isFetching && "animate-spin")} />
          {t("refresh")}
        </Button>
      </CardContent>
    </Card>
  );
}

function EventRow({
  event,
  onSelect,
}: {
  event: CheckInEventListItem;
  onSelect: () => void;
}) {
  const fmt = useFormat();
  const dateStr = fmt.dateTime(event.startsAt, {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
  const total = event.totalTickets;
  const checked = event.checkedInTickets;
  const pct = total > 0 ? Math.min(100, (checked / total) * 100) : 0;

  return (
    <button
      type="button"
      onClick={onSelect}
      className="w-full rounded-md border bg-card p-3 text-start transition-colors hover:border-primary/50 hover:bg-accent/30"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="truncate font-medium">{event.title}</div>
          <div className="text-xs text-muted-foreground">
            {dateStr}
            {event.venueName ? ` · ${event.venueName}` : ""}
          </div>
        </div>
        <div className="shrink-0 text-end text-xs">
          <div className="font-semibold">
            {fmt.number(checked)}/{fmt.number(total)}
          </div>
          <div className="text-muted-foreground">{fmt.percent(pct)}</div>
        </div>
      </div>
      <div className="mt-2 h-1 overflow-hidden rounded-full bg-muted">
        <div
          className="h-full bg-primary transition-[width]"
          style={{ width: `${pct}%` }}
        />
      </div>
    </button>
  );
}

/* ───────────── Scanner ───────────── */

interface ScannerScreenProps {
  eventId: string;
  manualMode: boolean;
  onToggleManual: () => void;
}

interface RecentScan {
  ts: number;
  result: ScanResult;
  ticketCode: string;
  ticketTypeName: string;
  buyerName: string;
}

const AUTO_RESUME_MS = 2500;
const DUPLICATE_WINDOW_MS = 2500;

function ScannerScreen({ eventId, manualMode, onToggleManual }: ScannerScreenProps) {
  const stats = useCheckInEventStatsQuery(eventId);
  const scan = useScanTicketMutation();
  const t = useT("checkIn");
  const fmt = useFormat();
  const [lastResponse, setLastResponse] = useState<ScanTicketResponse | null>(null);
  const [paused, setPaused] = useState(false);
  const [manualCode, setManualCode] = useState("");
  const [facingMode, setFacingMode] = useState<"environment" | "user">("environment");
  const [entryCount, setEntryCount] = useState(0);
  const [recent, setRecent] = useState<RecentScan[]>([]);
  const lastCodeRef = useRef<{ code: string; ts: number } | null>(null);

  const submit = useCallback(
    async (code: string) => {
      const trimmed = code.trim();
      if (!trimmed) return;
      try {
        const response = await scan.mutateAsync({
          ticketCode: trimmed,
          eventId,
        });
        setLastResponse({ ...response, message: describeScan(response, t, fmt) });
        setPaused(true);

        // Haptic + audio feedback. Telefonun ayarlarına göre çalışmayabilir,
        // sessizce fallback. Permission ve API mevcudiyetini fonksiyon içinde kontrol et.
        if (response.result === "Success") {
          beep("success");
          vibrate([80, 40, 80]);
          setEntryCount((c) => c + 1);
        } else {
          beep("error");
          vibrate([220, 80, 220]);
        }

        if (response.ticket) {
          setRecent((prev) =>
            [
              {
                ts: Date.now(),
                result: response.result,
                ticketCode: response.ticket!.ticketCode,
                ticketTypeName: response.ticket!.ticketTypeName,
                buyerName: response.ticket!.buyerName,
              },
              ...prev,
            ].slice(0, 3),
          );
        }
      } catch {
        beep("error");
        vibrate([220, 80, 220]);
        setLastResponse({
          result: "NotFound",
          message: t("networkError"),
          ticket: null,
        });
        setPaused(true);
      }
    },
    [eventId, scan, t, fmt],
  );

  const handleDetect = useCallback(
    (codes: IDetectedBarcode[]) => {
      if (paused || codes.length === 0) return;
      const value = codes[0].rawValue;
      const now = Date.now();
      // Aynı kodu kısa pencerede tekrar tetikleme — kamera framewise scanner sürekli okur.
      if (
        lastCodeRef.current &&
        lastCodeRef.current.code === value &&
        now - lastCodeRef.current.ts < DUPLICATE_WINDOW_MS
      ) {
        return;
      }
      lastCodeRef.current = { code: value, ts: now };
      void submit(value);
    },
    [paused, submit],
  );

  // Kullanıcı dismiss etmediyse kısa süre sonra otomatik resume.
  useEffect(() => {
    if (!paused) return;
    const timer = window.setTimeout(() => {
      setPaused(false);
      setLastResponse(null);
    }, AUTO_RESUME_MS);
    return () => window.clearTimeout(timer);
  }, [paused]);

  const handleManualSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    void submit(manualCode);
    setManualCode("");
  };

  function dismiss() {
    setPaused(false);
    setLastResponse(null);
  }

  function toggleCamera() {
    setFacingMode((m) => (m === "environment" ? "user" : "environment"));
  }

  // Yudiel scanner facingMode değişiminde stream'i baştan kurmalı; key prop ile force remount.
  const scannerKey = useMemo(() => `${eventId}:${facingMode}`, [eventId, facingMode]);

  return (
    <div className="space-y-3">
      <StatsCard
        stats={stats.data}
        loading={stats.isLoading}
        entryCount={entryCount}
      />

      <Card className="overflow-hidden">
        <CardContent className="space-y-3 p-3">
          {!manualMode ? (
            <div className="relative aspect-square overflow-hidden rounded-md border bg-black">
              <Scanner
                key={scannerKey}
                onScan={handleDetect}
                paused={paused}
                constraints={{ facingMode }}
                styles={{
                  container: { width: "100%", height: "100%" },
                  video: { width: "100%", height: "100%", objectFit: "cover" },
                }}
              />
              {/* Kamera viewport üstündeki tarama çerçevesi */}
              {!paused && !lastResponse ? (
                <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                  <div className="h-2/3 w-2/3 rounded-2xl border-2 border-white/80 shadow-[0_0_0_9999px_rgba(0,0,0,0.25)]" />
                </div>
              ) : null}

              {/* Sağ üstte kamera değiştir */}
              <button
                type="button"
                onClick={toggleCamera}
                className="absolute end-2 top-2 flex h-9 w-9 items-center justify-center rounded-full bg-black/45 text-white backdrop-blur-sm transition hover:bg-black/65"
                aria-label={t("switchCamera")}
              >
                <RotateCcw className="h-4 w-4" />
              </button>

              {/* Full-screen sonuç overlay'i */}
              {lastResponse ? (
                <FullScreenResult
                  response={lastResponse}
                  onDismiss={dismiss}
                />
              ) : null}
            </div>
          ) : (
            <form onSubmit={handleManualSubmit} className="space-y-2">
              <Label htmlFor="manual-code" className="text-sm">
                {t("ticketCode")}
              </Label>
              <Input
                id="manual-code"
                value={manualCode}
                onChange={(e) => setManualCode(e.target.value)}
                placeholder="XXXX-XXXX-XXXX"
                autoCapitalize="characters"
                autoComplete="off"
                spellCheck={false}
                className="font-mono uppercase tracking-wider"
                dir="ltr"
              />
              <Button
                type="submit"
                className="w-full"
                disabled={!manualCode.trim() || scan.isPending}
              >
                {scan.isPending ? (
                  <Loader2 className="me-1 h-4 w-4 animate-spin" />
                ) : (
                  <Search className="me-1 h-4 w-4" />
                )}
                {t("verify")}
              </Button>
              {/* Manuel modda sonuç inline kart olarak görünür */}
              {lastResponse ? (
                <ResultCard response={lastResponse} onDismiss={dismiss} />
              ) : null}
            </form>
          )}

          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              className="flex-1"
              onClick={onToggleManual}
            >
              {manualMode ? (
                <>
                  <Camera className="me-1 h-4 w-4" />
                  {t("camera")}
                </>
              ) : (
                <>
                  <Keyboard className="me-1 h-4 w-4" />
                  {t("manual")}
                </>
              )}
            </Button>
            {paused ? (
              <Button size="sm" className="flex-1" onClick={dismiss}>
                {t("next")}
              </Button>
            ) : null}
          </div>
        </CardContent>
      </Card>

      {recent.length > 0 ? <RecentList items={recent} /> : null}
    </div>
  );
}

/* ───────────── Stats ───────────── */

function StatsCard({
  stats,
  loading,
  entryCount,
}: {
  stats: { title: string; checkedInTickets: number; totalTickets: number } | undefined;
  loading: boolean;
  entryCount: number;
}) {
  const t = useT("checkIn");
  const tCommon = useT("common");
  const fmt = useFormat();
  if (loading || !stats) {
    return (
      <Card>
        <CardContent className="p-3 text-sm text-muted-foreground">
          {tCommon("loading")}
        </CardContent>
      </Card>
    );
  }
  const pct =
    stats.totalTickets > 0 ? (stats.checkedInTickets / stats.totalTickets) * 100 : 0;
  return (
    <Card>
      <CardContent className="p-3">
        <div className="flex items-center justify-between gap-2">
          <div className="min-w-0">
            <div className="truncate text-sm font-medium">{stats.title}</div>
            <div className="text-xs text-muted-foreground">
              {t("scanned", {
                checked: fmt.number(stats.checkedInTickets),
                total: fmt.number(stats.totalTickets),
              })}
              {entryCount > 0 ? (
                <span className="ms-2 inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold text-emerald-700">
                  <CheckCircle2 className="h-3 w-3" />
                  {t("sessionCount", { count: fmt.number(entryCount) })}
                </span>
              ) : null}
            </div>
          </div>
          <div className="text-end">
            <div className="text-2xl font-bold leading-none">{fmt.percent(pct)}</div>
          </div>
        </div>
        <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-muted">
          <div
            className="h-full bg-primary transition-[width]"
            style={{ width: `${pct}%` }}
          />
        </div>
      </CardContent>
    </Card>
  );
}

/* ───────────── Result (full-screen overlay & inline) ───────────── */

function FullScreenResult({
  response,
  onDismiss,
}: {
  response: ScanTicketResponse;
  onDismiss: () => void;
}) {
  const t = useT("checkIn");
  const cfg = RESULT_THEME[response.result];
  const Icon = cfg.icon;
  return (
    <button
      type="button"
      onClick={onDismiss}
      className={cn(
        "absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 px-6 text-center text-white animate-in fade-in zoom-in-95",
        cfg.overlayBg,
      )}
      aria-label={t("closeResult")}
    >
      <Icon className="h-20 w-20 drop-shadow-md" />
      <div className="text-2xl font-bold drop-shadow-md">{t(`result.${response.result}`)}</div>
      <div className="max-w-xs text-sm/snug text-white/95 drop-shadow">
        {response.message}
      </div>
      {response.ticket ? (
        <div className="mt-2 space-y-0.5 text-xs text-white/90">
          <div className="font-mono">{response.ticket.ticketCode}</div>
          {response.ticket.ticketTypeName ? (
            <div>{response.ticket.ticketTypeName}</div>
          ) : null}
          {response.ticket.buyerName && response.ticket.buyerName !== "-" ? (
            <div>{response.ticket.buyerName}</div>
          ) : null}
          {response.result === "AlreadyUsed" && response.ticket.checkedInByEmail ? (
            <div className="opacity-80">
              {response.ticket.checkedInByEmail}
            </div>
          ) : null}
        </div>
      ) : null}
      <div className="mt-3 rounded-full bg-white/20 px-4 py-1.5 text-xs font-medium">
        {t("tapToContinue")}
      </div>
    </button>
  );
}

function ResultCard({
  response,
  onDismiss,
}: {
  response: ScanTicketResponse;
  onDismiss: () => void;
}) {
  const t = useT("checkIn");
  const cfg = RESULT_THEME[response.result];
  const Icon = cfg.icon;
  return (
    <Card className={cn("border-2", cfg.border)} role="status" aria-live="polite">
      <CardContent className={cn("space-y-2 p-4", cfg.bg)}>
        <div className="flex items-center gap-3">
          <div
            className={cn(
              "flex h-10 w-10 shrink-0 items-center justify-center rounded-full",
              cfg.iconBg,
            )}
          >
            <Icon className={cn("h-6 w-6", cfg.iconFg)} />
          </div>
          <div className="min-w-0 flex-1">
            <div className={cn("font-semibold", cfg.title)}>{t(`result.${response.result}`)}</div>
            <div className="text-sm text-muted-foreground">{response.message}</div>
          </div>
          <Button size="sm" variant="ghost" onClick={onDismiss} className="-me-2">
            {t("ok")}
          </Button>
        </div>
        {response.ticket ? (
          <dl className="grid grid-cols-3 gap-x-2 gap-y-1 rounded-md bg-background/60 p-2 text-xs">
            <dt className="text-muted-foreground">{t("fields.ticket")}</dt>
            <dd className="col-span-2 font-mono">{response.ticket.ticketCode}</dd>
            <dt className="text-muted-foreground">{t("fields.type")}</dt>
            <dd className="col-span-2">{response.ticket.ticketTypeName}</dd>
            <dt className="text-muted-foreground">{t("fields.buyer")}</dt>
            <dd className="col-span-2">{response.ticket.buyerName}</dd>
            <dt className="text-muted-foreground">{t("fields.order")}</dt>
            <dd className="col-span-2">{response.ticket.orderNumber}</dd>
            {response.result === "AlreadyUsed" && response.ticket.checkedInByEmail ? (
              <>
                <dt className="text-muted-foreground">{t("fields.scannedBy")}</dt>
                <dd className="col-span-2">{response.ticket.checkedInByEmail}</dd>
              </>
            ) : null}
          </dl>
        ) : null}
      </CardContent>
    </Card>
  );
}

function RecentList({ items }: { items: RecentScan[] }) {
  const t = useT("checkIn");
  const fmt = useFormat();
  return (
    <Card>
      <CardContent className="space-y-2 p-3">
        <div className="flex items-center gap-2 text-xs font-semibold text-muted-foreground">
          <Ticket className="h-3.5 w-3.5" />
          {t("recent")}
        </div>
        <ul className="space-y-1.5">
          {items.map((it) => {
            const cfg = RESULT_THEME[it.result];
            const Icon = cfg.icon;
            return (
              <li
                key={`${it.ts}-${it.ticketCode}`}
                className="flex items-center gap-2 rounded-md border px-2 py-1.5 text-xs"
              >
                <Icon className={cn("h-4 w-4 shrink-0", cfg.iconFg)} />
                <div className="min-w-0 flex-1">
                  <div className="truncate font-mono">{it.ticketCode}</div>
                  <div className="truncate text-[11px] text-muted-foreground">
                    {it.ticketTypeName}
                    {it.buyerName && it.buyerName !== "-" ? ` · ${it.buyerName}` : ""}
                  </div>
                </div>
                <span className="text-[10px] text-muted-foreground">
                  {fmt.time(it.ts, { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
                </span>
              </li>
            );
          })}
        </ul>
      </CardContent>
    </Card>
  );
}

const SCAN_WHEN: Intl.DateTimeFormatOptions = {
  day: "2-digit",
  month: "short",
  hour: "2-digit",
  minute: "2-digit",
  timeZone: ISTANBUL_TZ,
};

/** Backend mesajı yalnızca Türkçe ve sunucu saatiyle; metni sonuç türünden kurarız. */
function describeScan(response: ScanTicketResponse, t: TFunction, fmt: Format): string {
  const ticket = response.ticket;
  switch (response.result) {
    case "AlreadyUsed": {
      if (!ticket?.usedAt) return t("message.AlreadyUsed");
      const when = fmt.dateTime(ticket.usedAt, SCAN_WHEN);
      return ticket.checkedInByEmail
        ? t("message.AlreadyUsedBy", { when, email: ticket.checkedInByEmail })
        : t("message.AlreadyUsedAt", { when });
    }
    case "WrongEvent":
      return ticket?.eventTitle
        ? t("message.WrongEventNamed", { event: ticket.eventTitle })
        : t("message.WrongEvent");
    default:
      return t(`message.${response.result}`);
  }
}

/* ───────────── Audio + haptic helpers ───────────── */

let _audioCtx: AudioContext | null = null;

function getAudioCtx(): AudioContext | null {
  if (typeof window === "undefined") return null;
  if (_audioCtx) return _audioCtx;
  // iOS Safari aksi takdirde sessize alır; user gesture içinde init olduğunda çalar.
  const Ctx =
    (window as unknown as { AudioContext?: typeof AudioContext })
      .AudioContext ??
    (window as unknown as { webkitAudioContext?: typeof AudioContext })
      .webkitAudioContext;
  if (!Ctx) return null;
  _audioCtx = new Ctx();
  return _audioCtx;
}

function beep(kind: "success" | "error") {
  const ctx = getAudioCtx();
  if (!ctx) return;
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.type = "sine";

  // Error için 2 alçak pip, success için tek tiz pip — kasiyerin gözüne bakmasa
  // bile sesle ayırt etsin.
  if (kind === "success") {
    osc.frequency.value = 1320;
    gain.gain.value = 0.18;
    osc.connect(gain).connect(ctx.destination);
    osc.start();
    osc.stop(ctx.currentTime + 0.12);
  } else {
    osc.frequency.value = 360;
    gain.gain.value = 0.22;
    osc.connect(gain).connect(ctx.destination);
    osc.start();
    osc.stop(ctx.currentTime + 0.18);
    // Kısa boşluk + ikinci pip
    const osc2 = ctx.createOscillator();
    const gain2 = ctx.createGain();
    osc2.type = "sine";
    osc2.frequency.value = 360;
    gain2.gain.value = 0.22;
    osc2.connect(gain2).connect(ctx.destination);
    osc2.start(ctx.currentTime + 0.24);
    osc2.stop(ctx.currentTime + 0.42);
  }
}

function vibrate(pattern: number[]) {
  if (typeof navigator === "undefined") return;
  if (typeof navigator.vibrate === "function") {
    try {
      navigator.vibrate(pattern);
    } catch {
      // Bazı tarayıcılar permission veya feature-policy ile engelleyebilir.
    }
  }
}

const RESULT_THEME: Record<
  ScanResult,
  {
    icon: typeof CheckCircle2;
    border: string;
    bg: string;
    iconBg: string;
    iconFg: string;
    title: string;
    overlayBg: string;
  }
> = {
  Success: {
    icon: CheckCircle2,
    border: "border-emerald-500/60",
    bg: "bg-emerald-50/70 dark:bg-emerald-950/30",
    iconBg: "bg-emerald-500/15",
    iconFg: "text-emerald-600 dark:text-emerald-400",
    title: "text-emerald-800 dark:text-emerald-200",
    overlayBg: "bg-emerald-500/95",
  },
  AlreadyUsed: {
    icon: AlertTriangle,
    border: "border-amber-500/60",
    bg: "bg-amber-50/70 dark:bg-amber-950/30",
    iconBg: "bg-amber-500/15",
    iconFg: "text-amber-600 dark:text-amber-400",
    title: "text-amber-800 dark:text-amber-200",
    overlayBg: "bg-amber-500/95",
  },
  Cancelled: {
    icon: Ban,
    border: "border-rose-500/60",
    bg: "bg-rose-50/70 dark:bg-rose-950/30",
    iconBg: "bg-rose-500/15",
    iconFg: "text-rose-600 dark:text-rose-400",
    title: "text-rose-800 dark:text-rose-200",
    overlayBg: "bg-rose-600/95",
  },
  NotFound: {
    icon: XCircle,
    border: "border-rose-500/60",
    bg: "bg-rose-50/70 dark:bg-rose-950/30",
    iconBg: "bg-rose-500/15",
    iconFg: "text-rose-600 dark:text-rose-400",
    title: "text-rose-800 dark:text-rose-200",
    overlayBg: "bg-rose-600/95",
  },
  NotAuthorized: {
    icon: ShieldAlert,
    border: "border-rose-500/60",
    bg: "bg-rose-50/70 dark:bg-rose-950/30",
    iconBg: "bg-rose-500/15",
    iconFg: "text-rose-600 dark:text-rose-400",
    title: "text-rose-800 dark:text-rose-200",
    overlayBg: "bg-rose-600/95",
  },
  WrongEvent: {
    icon: AlertTriangle,
    border: "border-amber-500/60",
    bg: "bg-amber-50/70 dark:bg-amber-950/30",
    iconBg: "bg-amber-500/15",
    iconFg: "text-amber-600 dark:text-amber-400",
    title: "text-amber-800 dark:text-amber-200",
    overlayBg: "bg-amber-500/95",
  },
};
